import React from 'react';
import { LEGACY_STORAGE_KEY, STORAGE_KEY } from '../utils/storage.js';

/**
 * Last line of defence around the whole app.
 *
 * Without one, any render error unmounts the tree and leaves a blank white
 * page — and because the most likely cause is malformed saved state, a reload
 * reproduces it forever. So the fallback offers a way out: clear the saved
 * data and start again.
 *
 * Class component because React has no hook equivalent of
 * `componentDidCatch`.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // No telemetry in this app — the console is where a bug report starts.
    console.error('Unhandled error:', error, info?.componentStack);
  }

  /** Wipe the saved notebook and reload. The nuclear option, clearly labelled. */
  handleReset = () => {
    try {
      // Both keys: a reset that left the legacy notebook behind would reload
      // straight back into the corrupt state it was meant to clear.
      window.localStorage.removeItem(STORAGE_KEY);
      window.localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch {
      // Nothing to do — if storage is unreachable it cannot be the culprit.
    }
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="mx-auto flex h-[100dvh] max-w-md flex-col items-center justify-center gap-4 px-6 text-center">
        <h1 className="font-display text-xl uppercase tracking-tight">the page tore</h1>

        <p className="font-hand text-xl text-ink-soft">
          something went wrong while drawing your notebook. reloading usually fixes it.
        </p>

        <div className="flex flex-wrap justify-center gap-2 pt-2">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-pill border border-accent bg-accent px-4 py-1.5 text-[10px]
                       uppercase tracking-widest text-white"
          >
            reload
          </button>

          <button
            type="button"
            onClick={this.handleReset}
            className="rounded-pill border border-line px-4 py-1.5 text-[10px] uppercase
                       tracking-widest text-ink-soft hover:border-ink"
          >
            clear saved data
          </button>
        </div>

        {/* The message itself, small — useful when reporting the problem */}
        <p className="pt-2 font-mono text-[10px] text-ink-soft">
          {String(this.state.error?.message || this.state.error)}
        </p>
      </div>
    );
  }
}
